import React, { useEffect, useRef, useState } from 'react';
import { Phone, PhoneOff, Mic, MicOff, Volume2, VolumeX, X, Video, VideoOff, SwitchCamera } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import AgoraRTC from 'agora-rtc-sdk-ng';
import { useLanguage } from '../context/LanguageContext';
import { getMediaUrl } from '../utils/mediaUrl';

/**
 * Voice / Video call modal (Agora RTC).
 *
 * Props:
 *  - peerUserId   : the user we want to call
 *  - peerName     : display name
 *  - peerAvatarUrl: optional
 *  - callType     : 'audio' (default) | 'video'
 *  - onClose      : closes the modal (called after end)
 */
const VoiceCallModal = ({ peerUserId, peerName, peerAvatarUrl, callType = 'audio', onClose }) => {
  const { t } = useLanguage();
  // Helper: if a translation key is missing, t() returns the key itself.
  // `tr` falls back to the provided default in that case so we never display raw i18n keys.
  const tr = (key, fallback) => {
    const v = t(key);
    return v && v !== key ? v : fallback;
  };
  const isVideo = callType === 'video';
  const [status, setStatus] = useState('connecting'); // connecting | ringing | active | ended | error
  const [errorMsg, setErrorMsg] = useState('');
  const [muted, setMuted] = useState(false);
  const [cameraOff, setCameraOff] = useState(false);
  const [speakerOn, setSpeakerOn] = useState(true);
  const [duration, setDuration] = useState(0);
  const [remoteJoined, setRemoteJoined] = useState(false);
  const [remoteHasVideo, setRemoteHasVideo] = useState(false);
  const [remainingQuota, setRemainingQuota] = useState(null); // seconds
  const [avatarFailed, setAvatarFailed] = useState(false);

  const clientRef = useRef(null);
  const localTrackRef = useRef(null); // mic
  const localVideoTrackRef = useRef(null); // camera (video calls only)
  const remoteVideoElRef = useRef(null);
  const localVideoElRef = useRef(null);
  const remoteVideoTrackRef = useRef(null);
  const sessionIdRef = useRef(null);
  const invitationIdRef = useRef(null);
  const startedAtRef = useRef(null);
  const ringingStartedAtRef = useRef(null);
  const timerRef = useRef(null);
  const ringingTimerRef = useRef(null);
  const statusPollRef = useRef(null);
  const remoteTrackRef = useRef(null);
  const endedOnceRef = useRef(false);

  // ---- Cleanup safe wrapper ----
  const cleanup = async () => {
    try {
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = null;
      if (ringingTimerRef.current) clearTimeout(ringingTimerRef.current);
      ringingTimerRef.current = null;
      if (statusPollRef.current) clearInterval(statusPollRef.current);
      statusPollRef.current = null;
      if (localTrackRef.current) {
        try { localTrackRef.current.stop(); } catch (_) {}
        try { localTrackRef.current.close(); } catch (_) {}
        localTrackRef.current = null;
      }
      if (localVideoTrackRef.current) {
        try { localVideoTrackRef.current.stop(); } catch (_) {}
        try { localVideoTrackRef.current.close(); } catch (_) {}
        localVideoTrackRef.current = null;
      }
      if (clientRef.current) {
        try { await clientRef.current.leave(); } catch (_) {}
        clientRef.current.removeAllListeners?.();
        clientRef.current = null;
      }
    } catch (e) { /* swallow */ }
  };

  // ---- End call (server side + local) ----
  const endCall = async (silent = false, reason = null) => {
    if (endedOnceRef.current) return;
    endedOnceRef.current = true;

    const elapsed = startedAtRef.current ? Math.floor((Date.now() - startedAtRef.current) / 1000) : 0;
    setStatus('ended');
    await cleanup();

    // If we never connected (still ringing/cancelled), cancel the invitation
    if (invitationIdRef.current && !startedAtRef.current) {
      try { await axios.post(`/api/calls/invitations/${invitationIdRef.current}/decline`); } catch (_) {}
    }

    if (sessionIdRef.current) {
      try {
        await axios.post('/api/calls/end', {
          session_id: sessionIdRef.current,
          duration_seconds: elapsed,
        });
      } catch (e) { /* ignore */ }
    }
    if (!silent) {
      if (reason === 'no_answer') {
        toast.info(tr('calls.no_answer') || 'Pas de réponse');
      } else if (reason === 'declined') {
        toast.info(tr('calls.declined') || 'Appel refusé');
      } else {
        toast.info(`${tr('calls.ended') || 'Appel terminé'} · ${formatTime(elapsed)}`);
      }
    }
    setTimeout(() => onClose && onClose(), 600);
  };

  // ---- Bootstrap call ----
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        // 1. Create invitation on backend (also sends push to callee)
        let inviteRes;
        try {
          inviteRes = await axios.post('/api/calls/invite', { peer_user_id: peerUserId, call_type: callType });
        } catch (err) {
          const code = err?.response?.status;
          const detail = err?.response?.data?.detail || '';
          if (code === 429) {
            setErrorMsg(tr('calls.quota_exceeded') || 'Vous avez atteint votre quota de 30 minutes d\'appel pour aujourd\'hui.');
          } else if (code === 503) {
            setErrorMsg(tr('calls.not_configured') || 'Les appels vocaux ne sont pas activés sur ce serveur.');
          } else if (code === 403) {
            setErrorMsg(tr('calls.peer_dnd') || 'Cet utilisateur a activé Ne pas déranger');
          } else if (code === 409) {
            setErrorMsg(tr('calls.peer_busy') || 'Cette personne est déjà en communication. Réessayez plus tard.');
          } else {
            setErrorMsg(detail || 'Erreur lors de la connexion');
          }
          setStatus('error');
          return;
        }

        if (cancelled) return;
        const { invitation_id, app_id, channel_name, token, uid, remaining_seconds, timeout_seconds } = inviteRes.data;
        invitationIdRef.current = invitation_id;
        setRemainingQuota(remaining_seconds);

        // 2. Create RTC client (we join immediately so we can talk as soon as B accepts)
        const client = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
        clientRef.current = client;

        client.on('user-published', async (remoteUser, mediaType) => {
          try {
            await client.subscribe(remoteUser, mediaType);
            if (mediaType === 'audio') {
              remoteTrackRef.current = remoteUser.audioTrack;
              remoteUser.audioTrack?.play();
              setRemoteJoined(true);
              setStatus('active');
              if (!startedAtRef.current) {
                startedAtRef.current = Date.now();
                startTimer();
                if (ringingTimerRef.current) clearTimeout(ringingTimerRef.current);
                if (statusPollRef.current) clearInterval(statusPollRef.current);
              }
            } else if (mediaType === 'video') {
              setRemoteHasVideo(true);
              remoteVideoTrackRef.current = remoteUser.videoTrack;
              // Play the remote video stream into the dedicated <div>. Retry a
              // few times because the container may not yet be mounted when
              // user-published fires (it's gated on status === 'active').
              const tryPlay = (attempt = 0) => {
                if (remoteVideoElRef.current && remoteUser.videoTrack) {
                  try { remoteUser.videoTrack.play(remoteVideoElRef.current); } catch (_) {}
                } else if (attempt < 10) {
                  setTimeout(() => tryPlay(attempt + 1), 200);
                }
              };
              tryPlay();
            }
          } catch (e) { /* ignore */ }
        });

        client.on('user-left', () => {
          // remote hung up
          endCall();
        });

        client.on('connection-state-change', (cur) => {
          if (cur === 'DISCONNECTED' || cur === 'FAILED') {
            if (!endedOnceRef.current) endCall();
          }
        });

        // 3. Join channel + publish local tracks (mic always, camera if video call)
        await client.join(app_id, channel_name, token, uid);
        // Microphone with aggressive echo + noise cancellation. The previous
        // {AEC,ANS,AGC:true} flags were the deprecated v3 API and were silently
        // ignored on v4+, causing the "cave" / echo the user reported.
        const audioTrack = await AgoraRTC.createMicrophoneAudioTrack({
          AEC: true, ANS: true, AGC: true,
          encoderConfig: 'speech_standard',
        });
        // Re-apply at the constraint level (v4+ canonical path) — the SDK reads
        // these from the underlying MediaStreamTrack.
        try {
          const ms = audioTrack.getMediaStreamTrack && audioTrack.getMediaStreamTrack();
          if (ms && ms.applyConstraints) {
            await ms.applyConstraints({
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true,
            });
          }
        } catch (_) { /* not fatal */ }
        localTrackRef.current = audioTrack;
        const tracksToPublish = [audioTrack];

        if (isVideo) {
          try {
            const videoTrack = await AgoraRTC.createCameraVideoTrack({
              encoderConfig: '480p_2',
              facingMode: 'user',
            });
            localVideoTrackRef.current = videoTrack;
            tracksToPublish.push(videoTrack);
            // Play preview into the local <div> once it mounts.
            setTimeout(() => {
              if (localVideoElRef.current) {
                try { videoTrack.play(localVideoElRef.current); } catch (_) {}
              }
            }, 80);
          } catch (camErr) {
            console.error('Camera init failed', camErr);
            toast.error("Caméra indisponible — l'appel continue en audio");
          }
        }

        await client.publish(tracksToPublish);

        setStatus('ringing');
        ringingStartedAtRef.current = Date.now();

        // 4. Backend session for quota accounting
        try {
          const startRes = await axios.post('/api/calls/start', {
            peer_user_id: peerUserId,
            channel_name,
          });
          sessionIdRef.current = startRes.data.session_id;
        } catch (e) { /* ignore */ }

        // 5. 30-second no-answer timeout
        const timeoutMs = (timeout_seconds || 30) * 1000;
        ringingTimerRef.current = setTimeout(() => {
          if (!endedOnceRef.current && !startedAtRef.current) {
            endCall(false, 'no_answer');
          }
        }, timeoutMs);

        // 6. Poll invitation status every 2 s — detects decline / missed
        statusPollRef.current = setInterval(async () => {
          if (!invitationIdRef.current || endedOnceRef.current) return;
          try {
            const r = await axios.get(`/api/calls/invitations/${invitationIdRef.current}`);
            const st = r?.data?.invitation?.status;
            if (st === 'declined') endCall(false, 'declined');
            else if (st === 'missed' || st === 'cancelled') endCall(false, 'no_answer');
          } catch (_) {}
        }, 2000);
      } catch (e) {
        console.error('Call init error:', e);
        if (!cancelled) {
          setErrorMsg(e?.message || 'Erreur d\'initialisation');
          setStatus('error');
        }
      }
    })();

    return () => {
      cancelled = true;
      cleanup();
    };
  }, [peerUserId]);

  // ---- Duration timer + auto-end at remaining quota ----
  const startTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      const elapsed = startedAtRef.current ? Math.floor((Date.now() - startedAtRef.current) / 1000) : 0;
      setDuration(elapsed);
      // Hard stop if elapsed reaches remaining quota
      if (remainingQuota !== null && elapsed >= remainingQuota) {
        toast.warning(tr('calls.quota_reached') || 'Quota quotidien atteint');
        endCall();
      }
    }, 1000);
  };

  const handleToggleMute = async () => {
    if (!localTrackRef.current) return;
    const next = !muted;
    try {
      await localTrackRef.current.setEnabled(!next);
      setMuted(next);
    } catch (_) {}
  };

  const handleToggleCamera = async () => {
    if (!localVideoTrackRef.current) return;
    const next = !cameraOff;
    try {
      await localVideoTrackRef.current.setEnabled(!next);
      setCameraOff(next);
    } catch (_) {}
  };

  const handleSwitchCamera = async () => {
    if (!localVideoTrackRef.current) return;
    try {
      const cameras = await AgoraRTC.getCameras();
      if (cameras.length < 2) return;
      const cur = localVideoTrackRef.current.getTrackLabel?.() || '';
      const other = cameras.find(c => c.label !== cur) || cameras[0];
      await localVideoTrackRef.current.setDevice(other.deviceId);
    } catch (_) {}
  };

  const handleToggleSpeaker = () => {
    setSpeakerOn(!speakerOn);
    if (remoteTrackRef.current) {
      try {
        remoteTrackRef.current.setVolume(!speakerOn ? 100 : 30);
      } catch (_) {}
    }
  };

  return (
    <div className={`fixed inset-0 z-[60] flex flex-col items-center justify-between p-6 ${
      isVideo ? 'bg-black' : 'bg-gradient-to-br from-violet-950 via-black to-violet-900'
    }`}
         data-testid="voice-call-modal"
         style={{
           paddingTop: 'max(2rem, env(safe-area-inset-top))',
           paddingBottom: 'max(2rem, env(safe-area-inset-bottom))',
         }}>
      {/* Remote video fills the screen during a video call. Rendered as soon as
          isVideo so the container exists when AgoraRTC's user-published-video
          fires (which can happen before status flips to 'active'). */}
      {isVideo && (
        <div
          ref={remoteVideoElRef}
          className={`absolute inset-0 bg-black z-0 ${status === 'active' && remoteHasVideo ? '' : 'invisible'}`}
          data-testid="remote-video-container"
        />
      )}
      {isVideo && status === 'active' && !remoteHasVideo && (
        <div className="absolute inset-0 flex items-center justify-center z-0 pointer-events-none">
          <div className="text-center text-white/60 text-sm">
            {peerName} {tr('calls.video_off') || "n'a pas activé sa caméra"}
          </div>
        </div>
      )}
      {/* Local preview (small) in corner during a video call */}
      {isVideo && (
        <div
          ref={localVideoElRef}
          className={`absolute top-20 right-4 w-24 h-32 sm:w-28 sm:h-36 rounded-xl overflow-hidden border-2 border-white/30 bg-black z-10 ${
            cameraOff ? 'opacity-30' : ''
          }`}
          data-testid="local-video-preview"
        />
      )}

      {/* Header */}
      <div className="w-full flex items-center justify-between relative z-20">
        <span className="text-xs text-white/60">
          {isVideo && (tr('calls.video_label') || 'Appel vidéo · ')}
          {status === 'connecting' && (tr('calls.connecting') || 'Connexion…')}
          {status === 'ringing' && (tr('calls.ringing') || 'Sonnerie…')}
          {status === 'active' && (tr('calls.in_call') || 'En appel')}
          {status === 'ended' && (tr('calls.ended') || 'Terminé')}
          {status === 'error' && (tr('calls.error') || 'Erreur')}
        </span>
        <button
          data-testid="voice-call-close-btn"
          onClick={() => endCall(true)}
          className="p-2 rounded-full hover:bg-white/10 text-white/70"
          aria-label="close">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Peer info (hidden when remote video is showing) */}
      {(!isVideo || !remoteHasVideo || status !== 'active') && (
      <div className="flex flex-col items-center gap-5 text-center relative z-20">
        <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-violet-500/40 bg-violet-700/30 flex items-center justify-center">
          {peerAvatarUrl && !avatarFailed ? (
            <img
              src={getMediaUrl(peerAvatarUrl)}
              alt=""
              className="w-full h-full object-cover"
              onError={() => setAvatarFailed(true)}
            />
          ) : (
            <span className="text-5xl font-bold text-white/80">{peerName?.[0]?.toUpperCase() || '?'}</span>
          )}
        </div>
        <h2 className="text-2xl font-semibold text-white" data-testid="voice-call-peer-name">{peerName}</h2>
        {status === 'active' && (
          <p className="text-4xl font-mono text-violet-200" data-testid="voice-call-timer">{formatTime(duration)}</p>
        )}
        {status === 'error' && (
          <p className="text-sm text-red-300 px-6 max-w-xs" data-testid="voice-call-error">{errorMsg}</p>
        )}
        {(status === 'connecting' || status === 'ringing') && (
          <p className="text-xs text-white/50 animate-pulse">
            {status === 'connecting'
              ? (tr('calls.preparing') || 'Préparation de l\'appel…')
              : (tr('calls.waiting') || 'En attente de la réponse…')}
          </p>
        )}
        {remainingQuota !== null && status === 'active' && (
          <p className="text-xs text-amber-300/80">
            {(tr('calls.daily_left') || 'Reste aujourd\'hui')} : {Math.floor(remainingQuota / 60)} min
          </p>
        )}
      </div>
      )}
      {/* Timer overlay for video calls when remote video is showing */}
      {isVideo && remoteHasVideo && status === 'active' && (
        <div className="absolute top-20 left-4 px-3 py-1.5 bg-black/60 rounded-full backdrop-blur-md z-20">
          <p className="text-sm font-mono text-white">{formatTime(duration)}</p>
        </div>
      )}

      {/* Controls */}
      <div className="flex items-center justify-center gap-4 sm:gap-6 relative z-20">
        {status === 'active' && (
          <>
            <button
              data-testid="voice-call-mute-btn"
              onClick={handleToggleMute}
              className={`w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center transition-all ${
                muted ? 'bg-red-600 hover:bg-red-700' : 'bg-white/10 hover:bg-white/20'
              }`}
              aria-label="mute">
              {muted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
            </button>
            {isVideo ? (
              <>
                <button
                  data-testid="voice-call-camera-btn"
                  onClick={handleToggleCamera}
                  className={`w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center transition-all ${
                    cameraOff ? 'bg-red-600 hover:bg-red-700' : 'bg-white/10 hover:bg-white/20'
                  }`}
                  aria-label="toggle camera">
                  {cameraOff ? <VideoOff className="w-6 h-6" /> : <Video className="w-6 h-6" />}
                </button>
                <button
                  data-testid="voice-call-switch-camera-btn"
                  onClick={handleSwitchCamera}
                  className="w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/20 transition-all"
                  aria-label="switch camera">
                  <SwitchCamera className="w-6 h-6" />
                </button>
              </>
            ) : (
              <button
                data-testid="voice-call-speaker-btn"
                onClick={handleToggleSpeaker}
                className={`w-14 h-14 sm:w-16 sm:h-16 rounded-full flex items-center justify-center transition-all ${
                  speakerOn ? 'bg-white/10 hover:bg-white/20' : 'bg-white/5 hover:bg-white/10 opacity-60'
                }`}
                aria-label="speaker">
                {speakerOn ? <Volume2 className="w-6 h-6" /> : <VolumeX className="w-6 h-6" />}
              </button>
            )}
          </>
        )}
        <button
          data-testid="voice-call-hangup-btn"
          onClick={() => endCall()}
          disabled={status === 'ended'}
          className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-red-600 hover:bg-red-700 flex items-center justify-center shadow-2xl shadow-red-900/50 transition-all disabled:opacity-50"
          aria-label="hangup">
          <PhoneOff className="w-7 h-7 sm:w-9 sm:h-9" />
        </button>
      </div>
    </div>
  );
};

const formatTime = (s) => {
  const m = Math.floor(s / 60);
  const ss = s % 60;
  return `${m}:${String(ss).padStart(2, '0')}`;
};

export default VoiceCallModal;
